/**
 * The dock's second chrome row, 36 px (spec §B1):
 *
 *   [Chart][Table][Cross-section] [hint / selection chip] …… [tab tools] | [◀ Worst ▶] [View ▾] [Export ▾] [⋯]
 *
 * The tablist follows the WAI-ARIA APG "Tabs" pattern with MANUAL activation:
 * one tab stop (the active tab), ←/→ move focus between tabs, Home/End jump to
 * the ends, Enter/Space activate. Manual rather than automatic because two of
 * these tabs are paywalled — a tab that opened the upgrade dialog merely
 * because an arrow key passed over it would be hostile — and because each
 * activation re-lays-out the whole dock body.
 *
 * Tabs that cannot open are `aria-disabled`, never the HTML `disabled`
 * attribute: that takes the tab out of the arrow sequence and out of hover,
 * so the reason in its `title` could never reach the reader. A LOCKED tab
 * still takes the click — it is the one place the upgrade path is
 * discoverable from — and carries an `UpgradeBadge`.
 *
 * The bar is an `@container`, so the tools in its right slot can compact
 * themselves (`DockStepper` drops its label below 1000 px).
 */
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { UpgradeBadge } from '@/components/subscription/FeatureGate';
import type { FeatureGate } from '@/lib/featureGates';
import { cn } from '@/lib/utils';

import { DOCK_CONTROL } from './dockStyles';
import { rovingTarget } from './rovingFocus';

export interface DockTab<T extends string = string> {
  id: T;
  label: string;
  /**
   * A DATA or LAYOUT reason the tab cannot open ("too narrow for the chart").
   * The tab goes `aria-disabled`, muted, inert, with this as its `title`.
   */
  disabledReason?: string | null;
  /**
   * The tab's subscription gate when it is LOCKED (pass `null`/omit when the
   * gate is open). The tab is muted and `aria-disabled` but its click calls
   * `onLockedClick` — the upgrade prompt — and it shows an `UpgradeBadge`.
   */
  lockedGate?: FeatureGate | null;
}

export interface DockTabBarProps<T extends string> {
  /**
   * `clearance` / `sv`: tabs are `${prefix}-tab-${id}` (id AND test id), and
   * point `aria-controls` at `${prefix}-tabpanel` — `DockBody`'s id.
   */
  testIdPrefix: string;
  /** The tablist's accessible name — the dock's title. */
  ariaLabel: string;
  tabs: DockTab<T>[];
  activeId: T;
  onSelect: (id: T) => void;
  /** A locked tab was activated — open the paywall. */
  onLockedClick?: (id: T) => void;
  /**
   * After the tablist, BEFORE the spacer: a caption on the tabs (the
   * "too narrow" hint) and the `DockSelectionChip`. A SIBLING of the tablist,
   * never a child — a `role="tablist"` may only contain tabs, and a stray span
   * inside it is announced as a broken tab.
   */
  start?: ReactNode;
  /** The right end: a `<DockToolbar>` of tab-contextual tools, a separator,
   *  then the dock-wide ones. */
  right?: ReactNode;
  testId?: string;
}

/**
 * The tool strip at the bar's right end. On a dock too narrow for every tool
 * it SCROLLS sideways rather than clipping (a clipped VE ▾ or Overlays on the
 * Section tab was simply unreachable at 520–700 px):
 *
 *  - The inner row grows to the strip's width and right-aligns its tools
 *    while they fit; once they do not, it is as wide as its content and the
 *    strip scrolls. (A plain `justify-end` on the scroll box itself would
 *    push the overflow off the LEFT edge, out of reach of any scroll.)
 *  - Pinned to the end by default, so the dock-wide tools (View, Export, ⋯)
 *    stay in view; the contextual ones are a scroll — or a Tab, which scrolls
 *    the focused control into view — away. Once the reader scrolls, it stays
 *    where they left it until they come back to the end.
 *  - A fade on whichever edge has more tools beyond it says so; the scrollbar
 *    itself is hidden (it would take the bar's height).
 *  - `py-1`: a scroll box clips both axes, and the controls' focus ring sits
 *    outside their 28 px — the padding is the ring's room.
 */
const DockTabBarTools = ({ children }: { children: ReactNode }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  /** Follow the end until the reader scrolls away from it. */
  const pinnedEnd = useRef(true);
  const [edges, setEdges] = useState<{ left: boolean; right: boolean }>({ left: false, right: false });
  const measure = (el: HTMLDivElement) => {
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 1) {
      setEdges((e) => (e.left || e.right ? { left: false, right: false } : e));
      return;
    }
    if (pinnedEnd.current) el.scrollLeft = max;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft < max - 1;
    setEdges((e) => (e.left === left && e.right === right ? e : { left, right }));
  };
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure(el);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure(el));
    ro.observe(el);
    // The CONTENT changes width too (a tab's contextual tools come and go,
    // a trigger's label changes) — observe the row inside as well.
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  });
  const mask =
    edges.left || edges.right
      ? `linear-gradient(to right, ${edges.left ? 'transparent 0, #000 16px' : '#000 0'}, ${
          edges.right ? '#000 calc(100% - 16px), transparent 100%' : '#000 100%'
        })`
      : undefined;
  return (
    <div
      ref={ref}
      data-testid='dock-tabbar-tools'
      data-overflow-left={edges.left ? 'true' : undefined}
      data-overflow-right={edges.right ? 'true' : undefined}
      onScroll={(e) => {
        const el = e.currentTarget;
        pinnedEnd.current = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1;
        measure(el);
      }}
      // On a phone the tools have the bar's second line to themselves, so they
      // wrap there rather than scroll: a scrolled strip left its first tool
      // cut in half at the faded edge ("Auto (1×)").
      className='ml-auto flex min-w-0 shrink overflow-x-auto overflow-y-hidden py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden @max-[560px]:basis-full @max-[560px]:overflow-visible'
      style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
    >
      <div className='flex shrink-0 grow items-center justify-end gap-1.5 @max-[560px]:shrink @max-[560px]:flex-wrap @max-[560px]:gap-y-1'>
        {children}
      </div>
    </div>
  );
};

export const DockTabBar = <T extends string>({
  testIdPrefix,
  ariaLabel,
  tabs,
  activeId,
  onSelect,
  onLockedClick,
  start,
  right,
  testId,
}: DockTabBarProps<T>) => {
  /** Filled by the tabs' ref callbacks — a commit-time write, which is what
   *  the React Compiler's no-refs-during-render rule permits. */
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());
  // The roving tab stop is the ACTIVE tab; if the active id is somehow not
  // among the tabs (a tab removed under it), the first tab keeps the stop so
  // the tablist is never unreachable by Tab.
  const stopId = tabs.some((tab) => tab.id === activeId) ? activeId : tabs[0]?.id;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const focused = tabs.findIndex((tab) => tabRefs.current.get(tab.id) === e.target);
    if (focused < 0) return;
    const next = rovingTarget(e.key, focused, tabs.length, 'horizontal');
    if (next === null) return;
    e.preventDefault();
    tabRefs.current.get(tabs[next].id)?.focus();
  };

  return (
    // Two boxes because an element cannot query its own size: the outer one is
    // the `@container`, the row inside reads it. Below 560 px (a phone) the row
    // wraps — tabs + the `start` controls on the first line, the tools on a
    // second. In one row the tools strip was squeezed to nothing (View /
    // Export / ⋯ unreachable) and the `start` controls painted over it.
    <div data-testid={testId} className='@container shrink-0 border-b border-slate-200 text-xs dark:border-stone-700'>
      <div className='flex h-9 items-center gap-3 px-3 @max-[560px]:h-auto @max-[560px]:flex-wrap @max-[560px]:gap-y-0'>
        {/* Who gives way on a short bar: never the tablist (a clipped tab label
          names nothing); the `start` slot truncates (the selection chip's
          range); the tools scroll. */}
        <div className='flex h-full min-w-0 shrink items-center gap-2 @max-[560px]:h-9 @max-[560px]:basis-full'>
          <div
            role='tablist'
            aria-label={ariaLabel}
            onKeyDown={onKeyDown}
            className='flex h-full shrink-0 items-stretch'
          >
            {tabs.map((tab) => {
              const isActive = tab.id === activeId;
              const locked = !!tab.lockedGate;
              const inert = locked || !!tab.disabledReason;
              return (
                <button
                  key={tab.id}
                  ref={(el) => {
                    if (el) tabRefs.current.set(tab.id, el);
                    else tabRefs.current.delete(tab.id);
                  }}
                  type='button'
                  role='tab'
                  id={`${testIdPrefix}-tab-${tab.id}`}
                  data-testid={`${testIdPrefix}-tab-${tab.id}`}
                  aria-controls={`${testIdPrefix}-tabpanel`}
                  aria-selected={isActive}
                  aria-disabled={inert || undefined}
                  data-disabled={inert ? 'true' : undefined}
                  data-locked={locked ? 'true' : undefined}
                  tabIndex={tab.id === stopId ? 0 : -1}
                  title={tab.disabledReason ?? undefined}
                  onClick={() => {
                    if (locked) {
                      onLockedClick?.(tab.id);
                      return;
                    }
                    if (tab.disabledReason) return;
                    onSelect(tab.id);
                  }}
                  className={cn(
                    // `-mb-px` + `border-b-2`: the active tab's underline sits ON
                    // the bar's own bottom rule rather than a pixel above it.
                    '-mb-px flex items-center gap-1 border-b-2 px-2 whitespace-nowrap focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none focus-visible:ring-inset',
                    isActive
                      ? 'border-current font-semibold text-slate-900 dark:text-white'
                      : 'border-transparent text-slate-500 hover:text-slate-900 dark:text-stone-400 dark:hover:text-white',
                    inert && DOCK_CONTROL.inert,
                    locked ? 'cursor-pointer' : tab.disabledReason ? 'cursor-not-allowed' : '',
                  )}
                >
                  {tab.label}
                  {tab.lockedGate && <UpgradeBadge gate={tab.lockedGate} />}
                </button>
              );
            })}
          </div>
          {start != null && start !== false && <div className='flex min-w-0 shrink items-center gap-2'>{start}</div>}
        </div>
        {/* On a dock too narrow for every tool the strip scrolls (see
          `DockTabBarTools`) — never clips a tool out of reach. */}
        {right != null && <DockTabBarTools>{right}</DockTabBarTools>}
      </div>
    </div>
  );
};
